import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Added import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { PriorityInsights } from '@components/interview/analysis-components/ProrityInsight';
import { Summary } from '@components/interview/analysis-components/Summary';
import { getQuoteText } from '@utils/representativeQuotes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For meaning
import NumbersIcon from '@mui/icons-material/Numbers';
import PsychologyIcon from '@mui/icons-material/Psychology'; // For qualities
import WbSunnyIcon from '@mui/icons-material/WbSunny'; // Example icon for Phenomenology
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useParams } from 'react-router';
import { AnalysisLayout } from './layout';

/** -------------------------------------------------------------
 * PhenomenologicalAnalysis – displays a portfolio-level Phenomenological analysis
 * ------------------------------------------------------------*/
export default function PhenomenologicalAnalysis({ item }) {
  const [expandedTheme, setExpandedTheme] = React.useState(false);
  const { id } = useParams();
  if (!item) return null;

  // Attempt to get the results object
  let results = {};
  if (typeof item.results === 'object' && item.results !== null) {
    results = item.results; // Use directly if already an object
  } else if (typeof item.results === 'string') {
    try {
      results = JSON.parse(item.results); // Try parsing if it's a string
    } catch (err) {
      console.error('Failed to parse results JSON string:', err, 'JSON String:', item.results);
      results = {}; // Assign empty object on parsing error
    }
  } else {
    // Handle cases where results is neither object nor string (e.g., undefined, null)

    console.warn('Results field is not an object or string:', item.results);
  }

  // Fallback if results are invalid or missing the required 'summary'
  if (Object.keys(results).length === 0 || !results.summary) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Analysis Data Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The results for this Phenomenological analysis could not be loaded or are improperly
            formatted.
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Show Raw Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(item, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  }

  /* ───────────────────────── Top Qualities card ───────────────────────── */
  const TopQualities = () => {
    if (!results.top_qualities?.length > 0) return null;
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <PsychologyIcon color="secondary" /> Top Qualities
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {results.top_qualities.map((quality, index) => (
              <Chip
                key={index}
                label={`${quality.quality} (${quality.frequency})`}
                size="small"
                variant="outlined"
                color="secondary"
                sx={{ textTransform: 'capitalize' }}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  /* ──────────────────────── Theme Prevalence Accordions ─────────────────────── */
  const ThemePrevalence = () => {
    if (!results.theme_prevalence?.length > 0) return null;

    const handleChange = (panel) => (event, isExpanded) => {
      setExpandedTheme(isExpanded ? panel : false);
    };

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Thematic Breakdown
        </Typography>
        {results.theme_prevalence.map((themeData, index) => {
          const panelId = `theme-panel-${index}`;
          return (
            <Accordion
              key={panelId}
              expanded={expandedTheme === panelId}
              onChange={handleChange(panelId)}
              sx={{ mb: 1 }}
              variant="outlined"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}
              >
                <Typography variant="subtitle1" sx={{ flexShrink: 2, fontWeight: 500 }}>
                  {themeData.theme}
                </Typography>
                <Chip
                  icon={<NumbersIcon />}
                  label={`Prevalence: ${themeData.prevalence}`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                {/* Representative Meaning */}
                {themeData.representative_meaning && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <InfoOutlinedIcon fontSize="small" color="action" /> Representative Meaning
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'text.secondary', pl: 3 }}
                    >
                      {themeData.representative_meaning}
                    </Typography>
                  </Box>
                )}

                {/* Common Qualities */}
                {themeData.common_qualities?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <PsychologyIcon fontSize="small" color="secondary" /> Common Qualities
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ pl: 3 }}
                    >
                      {themeData.common_qualities.map((quality, i) => (
                        <Chip
                          key={i}
                          label={quality}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Representative Quotes */}
                {themeData.representative_quotes?.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <FormatQuoteIcon fontSize="small" color="action" /> Representative Quotes
                    </Typography>
                    <List dense disablePadding sx={{ pl: 3 }}>
                      {themeData.representative_quotes.map((quote, i) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <FormatQuoteIcon
                              sx={{ fontSize: 16, transform: 'scaleX(-1)', color: 'text.disabled' }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={getQuoteText(quote)}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };
  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Phenomenological Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <WbSunnyIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Phenomenological Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary results={results} item={item} />
        <TopQualities />
        <PriorityInsights results={results} />
        <ThemePrevalence />
        <IncludedTranscripts item={item} id={id} />
      </CardContent>
    </AnalysisLayout>
  );
}

PhenomenologicalAnalysis.propTypes = {
  item: PropTypes.object.isRequired,
};
